/**
 * loadReconcileData — Datenbeschaffung für den Abgleich-Tab (BR-6).
 *
 * Bündelt die in §11 aufgeführten Loader (invoices + payments + dunning →
 * `toMatchableInvoices`; expenses + received_invoices-Join → `toMatchableExpenses`;
 * `bank_transactions` unmatched + matched; `bank_accounts`) und liefert dem UI
 * fertige Engine-Shapes plus Lookup-Maps (für Confirm-/Unlink-/Reversal-Flows,
 * die die Original-`Payment`-Docs brauchen).
 *
 * Owner-scoped: alle Reads laufen über `effectiveScopeUid` bzw. die
 * owner-scoped `bankApi`/`paymentsApi`/`expensesApi`-Loader.
 *
 * @module loadReconcileData
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  listTransactions,
  listBankAccounts,
} from '../../api/bankApi';
import { getPaymentsForUser } from '../../api/paymentsApi';
import { getExpensesForUser } from '../../api/expensesApi';
import { getAllDunningEventsForUser } from '../../api/dunningApi';
import { toMatchableInvoices, toMatchableExpenses } from '../../utils/bankMatching';
import type {
  MatchableInvoice,
  MatchableExpense,
  InvoiceDoc,
} from '../../utils/bankMatching';
import {
  BankAccount,
  BankTransaction,
  BankTxMatchStatus,
} from '../../types/bankTypes';
import { Payment } from '../../types/paymentTypes';
import { DunningEvent } from '../../types/dunningTypes';
import { Expense } from '../../types/expenseTypes';
import { ReceivedInvoice } from '../../types/receivedInvoiceTypes';

/** Hard cap on paginated transaction loading (keeps the client-side pass bounded). */
const TX_PAGE_SIZE = 300;
const TX_MAX_PAGES = 20;

/** Fully assembled data set the Abgleich-/„Ohne Beleg"-Tabs consume. */
export interface ReconcileData {
  accounts: BankAccount[];
  /** Unmatched transactions (both signs). */
  unmatchedTxs: BankTransaction[];
  /** All loaded transactions (unmatched + matched) — internal-transfer mirror search. */
  allTxs: BankTransaction[];
  /** Matched credits with `paymentIds.length ≥ 1` — reversal detection basis (§8.9). */
  matchedCreditTxs: BankTransaction[];
  invoices: MatchableInvoice[];
  expenses: MatchableExpense[];
  /** paymentId → Payment (for unlink / reversal storno lookups). */
  paymentById: Map<string, Payment>;
  invoiceById: Map<string, MatchableInvoice>;
  expenseById: Map<string, MatchableExpense>;
  txById: Map<string, BankTransaction>;
}

/** Returns today's date as `YYYY-MM-DD` (local timezone, Muster OpenItemsPage). */
function getTodayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Loads all transactions for a match status via cursor pagination (bounded). */
async function loadAllTransactions(matchStatus: BankTxMatchStatus): Promise<BankTransaction[]> {
  const out: BankTransaction[] = [];
  let cursor: unknown = undefined;
  for (let page = 0; page < TX_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- sequential cursor pagination
    const res = await listTransactions({ matchStatus, limit: TX_PAGE_SIZE, cursor });
    out.push(...res.txs);
    if (!res.nextCursor) break;
    cursor = res.nextCursor;
  }
  return out;
}

/**
 * Loads and maps everything the Abgleich-Tab needs.
 *
 * @param uid - The effective owner scope UID (owner-only, §7.1).
 * @returns Fully assembled {@link ReconcileData}.
 * @throws {Error} If any owner-scoped read fails (surfaced as a toast/retry in the UI).
 */
export async function loadReconcileData(uid: string): Promise<ReconcileData> {
  const todayISO = getTodayISO();

  const [accounts, unmatchedTxs, matchedTxs, allPayments, allEvents, allExpenses] =
    await Promise.all([
      listBankAccounts(),
      loadAllTransactions('unmatched'),
      loadAllTransactions('matched'),
      getPaymentsForUser(uid),
      getAllDunningEventsForUser(uid),
      getExpensesForUser(uid),
    ]);

  // Invoices (own query, Muster OpenItemsPage) — owner-scoped.
  const invoicesSnap = await getDocs(
    query(collection(db, 'invoices'), where('userId', '==', uid)),
  );
  const invoiceDocs: InvoiceDoc[] = invoicesSnap.docs.map(
    (d) => ({ id: d.id, ...(d.data() as Omit<InvoiceDoc, 'id'>) }),
  );

  // received_invoices (IBAN-Join für Ausgaben-Matching) — owner-scoped.
  const receivedSnap = await getDocs(
    query(collection(db, 'received_invoices'), where('userId', '==', uid)),
  );
  const receivedById = new Map<string, ReceivedInvoice>();
  receivedSnap.docs.forEach((d) => {
    receivedById.set(d.id, { id: d.id, ...(d.data() as Omit<ReceivedInvoice, 'id'>) } as ReceivedInvoice);
  });

  const allTxs = [...unmatchedTxs, ...matchedTxs];
  const matchedCreditTxs = matchedTxs.filter(
    (tx) => tx.amountCents > 0 && (tx.paymentIds?.length ?? 0) >= 1,
  );

  // payments grouped by invoiceId (mapper) + by id (unlink/reversal lookup).
  const paymentsByInvoice = new Map<string, Payment[]>();
  const paymentById = new Map<string, Payment>();
  for (const p of allPayments) {
    const arr = paymentsByInvoice.get(p.invoiceId) ?? [];
    arr.push(p);
    paymentsByInvoice.set(p.invoiceId, arr);
    if (p.id) paymentById.set(p.id, p);
  }

  // dunning events grouped by invoiceId (mapper).
  const dunningByInvoice = new Map<string, DunningEvent[]>();
  for (const evt of allEvents) {
    const arr = dunningByInvoice.get(evt.invoiceId) ?? [];
    arr.push(evt);
    dunningByInvoice.set(evt.invoiceId, arr);
  }

  // alreadyMatched-Set: any loaded tx that lists the expense in matchedExpenseIds.
  const matchedExpenseIdSet = new Set<string>();
  for (const tx of allTxs) {
    for (const eid of tx.matchedExpenseIds ?? []) {
      matchedExpenseIdSet.add(eid);
    }
  }

  const invoices = toMatchableInvoices(invoiceDocs, paymentsByInvoice, dunningByInvoice, todayISO);
  const expenses = toMatchableExpenses(allExpenses as Expense[], receivedById, matchedExpenseIdSet);

  const invoiceById = new Map(invoices.map((inv) => [inv.invoiceId, inv]));
  const expenseById = new Map(expenses.map((exp) => [exp.expenseId, exp]));
  const txById = new Map(allTxs.map((tx) => [tx.id, tx]));

  return {
    accounts,
    unmatchedTxs,
    allTxs,
    matchedCreditTxs,
    invoices,
    expenses,
    paymentById,
    invoiceById,
    expenseById,
    txById,
  };
}
